import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { Settings, RefreshCw, DollarSign, KeyRound, Eye, EyeOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ headers: { Authorization: 'Bearer ' + localStorage.getItem('admin_token') }, withCredentials: true });

export default function AdminSettings() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [manualRate, setManualRate] = useState('');
  const [useBnrAuto, setUseBnrAuto] = useState(true);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(API + '/admin/settings/exchange-rate', authHeaders());
      setSettings(response.data);
      setManualRate(response.data.manual_rate || 5.0);
      setUseBnrAuto(response.data.use_bnr_auto ?? true);
    } catch (error) {
      toast.error('Eroare la incarcarea setarilor');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await axios.put(API + '/admin/settings/exchange-rate', {
        use_bnr_auto: useBnrAuto,
        manual_rate: parseFloat(manualRate)
      }, authHeaders());
      setSettings(response.data);
      toast.success('Setari salvate');
    } catch (error) {
      toast.error('Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      toast.error('Completati parola curenta si parola noua');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Parola noua trebuie sa aiba minim 6 caractere');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Parola noua si confirmarea nu coincid');
      return;
    }
    setChangingPassword(true);
    try {
      await axios.put(API + '/admin/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      }, authHeaders());
      toast.success('Parola a fost schimbata. Va rugam sa va reconectati.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      // Force re-login after password change
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      setTimeout(() => navigate('/admin/login'), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la schimbarea parolei');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="h-8 w-8 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Setari</h1>
        <p className="text-slate-500">Configurare curs valutar EUR/RON</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Schimbare Parola */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-rose-600" />
              Schimbare parola
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Parola curenta</Label>
              <div className="relative">
                <Input
                  data-testid="current-password-input"
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Introduceti parola curenta"
                  autoComplete="off"
                />
                <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Parola noua</Label>
              <div className="relative">
                <Input
                  data-testid="new-password-input"
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minim 6 caractere"
                  autoComplete="off"
                />
                <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Confirmare parola noua</Label>
              <Input
                data-testid="confirm-password-input"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Reintroduceti parola noua"
                autoComplete="off"
              />
            </div>
            <Button
              data-testid="change-password-btn"
              onClick={handleChangePassword}
              disabled={changingPassword}
              className="w-full bg-rose-600 hover:bg-rose-700 text-white"
            >
              {changingPassword ? 'Se schimba...' : 'Schimba parola'}
            </Button>
          </CardContent>
        </Card>

        {/* Curs Valutar */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-blue-600" />
              Curs Valutar EUR/RON
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-base font-medium">Foloseste curs BNR automat</Label>
                <p className="text-sm text-slate-500">Cursul se preia automat de la BNR</p>
              </div>
              <Switch
                data-testid="bnr-auto-toggle"
                checked={useBnrAuto}
                onCheckedChange={setUseBnrAuto}
              />
            </div>

            {!useBnrAuto && (
              <div className="space-y-2">
                <Label>Curs manual EUR → RON</Label>
                <Input
                  data-testid="manual-rate-input"
                  type="number"
                  step="0.0001"
                  value={manualRate}
                  onChange={(e) => setManualRate(e.target.value)}
                  placeholder="ex: 5.0000"
                />
              </div>
            )}

            <Button
              data-testid="save-settings-btn"
              onClick={saveSettings}
              disabled={saving}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              {saving ? 'Se salveaza...' : 'Salveaza setarile'}
            </Button>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-emerald-600" />
              Status Curs Curent
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {settings && (
              <>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">Curs BNR</p>
                  <p className="text-2xl font-bold text-blue-900">{settings.bnr_rate ? `${settings.bnr_rate} RON/EUR` : 'Indisponibil'}</p>
                  {settings.bnr_last_updated && (
                    <p className="text-xs text-blue-600 mt-1">Actualizat: {new Date(settings.bnr_last_updated).toLocaleString('ro-RO')}</p>
                  )}
                </div>

                <div className="p-4 bg-emerald-50 rounded-lg">
                  <p className="text-sm text-emerald-700">Curs activ (folosit)</p>
                  <p className="text-2xl font-bold text-emerald-900">{settings.current_rate} RON/EUR</p>
                  <p className="text-xs text-emerald-600">Sursa: {useBnrAuto ? 'BNR Automat' : 'Manual'}</p>
                </div>

                <div className="p-4 bg-amber-50 rounded-lg">
                  <p className="text-sm text-amber-700">Curs cu marja (+1%)</p>
                  <p className="text-2xl font-bold text-amber-900">{settings.current_rate_with_margin} RON/EUR</p>
                  <p className="text-xs text-amber-600">Acesta este cursul aplicat la facturare</p>
                </div>

                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">Curs manual configurat</p>
                  <p className="text-lg font-bold text-slate-800">{settings.manual_rate} RON/EUR</p>
                </div>
              </>
            )}

            <Button
              variant="outline"
              onClick={fetchSettings}
              className="w-full"
              data-testid="refresh-rate-btn"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Reincarca cursul
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
